import { ArrowUp, Eye, EyeOff, ImagePlus, Plus, Star, Trash2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { MAX_UPLOAD_MB, mimeOf, uploadOriginal } from '../lib/upload';
import { Card, Field, VisibleToggle, btnGhost, btnSecondary, inputCls } from './ui';

/** Frase standard del sito (apps/web, dizionari `guests.defaultWelcome`). */
const DEFAULT_WELCOME =
  'Benvenuti! Ecco cosa c’è da fare intorno a noi in questi giorni: eventi, luoghi da vedere e il mare giusto per oggi.';

/** Immagine bassa in testa (`media/guests/<slug>/<id>`). */
export interface GuestBanner {
  key: string;
  width: number;
  height: number;
}

export type ButtonKind =
  | 'wifi'
  | 'info'
  | 'orario'
  | 'colazione'
  | 'checkout'
  | 'luogo'
  | 'link'
  | 'whatsapp'
  | 'telefono'
  | 'naviga';

/** Tasto come lo salva l'API (`GuestButton`). */
export interface GuestButton {
  id: string;
  kind: ButtonKind;
  label: string;
  value: string;
  tap: 'none' | 'link' | 'popup';
  url?: string;
  popupTitle?: string;
  popupText?: string;
  ssid?: string;
  pass?: string;
  number?: string;
  featured: boolean;
  /** nascosto: resta qui ma non si vede sulla pagina */
  hidden?: boolean;
}

/**
 * Tipi di tasto: nome nel pannello, etichetta e testo di partenza; `fixed` = cosa fa al tocco,
 * sempre (gli altri: niente, link esterno o popup, a scelta).
 */
const KINDS: Record<
  ButtonKind,
  { name: string; label: string; value: string; fixed?: 'wifi' | 'number' | 'nav'; note?: string }
> = {
  // label e value: i testi standard del sito (apps/web, dizionari `guests.buttonDefaults`), già
  // tradotti nelle cinque lingue: si usano se la struttura lascia i campi vuoti
  wifi: { name: 'Wi-Fi', label: 'Wi-Fi', value: 'Rete e password', fixed: 'wifi' },
  info: { name: 'Informazione', label: 'Da sapere', value: '' },
  orario: { name: 'Orario', label: 'Check-in', value: '' },
  colazione: { name: 'Colazione', label: 'Colazione', value: '' },
  checkout: { name: 'Check-out', label: 'Check-out', value: '' },
  luogo: { name: 'Luogo', label: 'Parcheggio', value: '' },
  link: { name: 'Pagina web', label: 'Sito web', value: 'Apri' },
  whatsapp: {
    name: 'WhatsApp',
    label: 'WhatsApp',
    value: 'Scriveteci',
    fixed: 'number',
    note: 'Apre WhatsApp con questo numero.',
  },
  telefono: {
    name: 'Telefono',
    label: 'Telefono',
    value: 'Chiamateci',
    fixed: 'number',
    note: 'Chiama questo numero.',
  },
  naviga: {
    name: 'Navigatore',
    label: 'Torna in struttura',
    value: 'Apri il navigatore',
    fixed: 'nav',
  },
};
const KIND_ORDER = Object.keys(KINDS) as ButtonKind[];
export const MAX_BUTTONS = 7;

const newId = () => Math.random().toString(36).slice(2, 8).padEnd(6, '0');
const blank = (kind: ButtonKind): GuestButton => ({
  id: newId(),
  kind,
  // vuoti: i testi standard, già tradotti
  label: '',
  value: '',
  tap: 'none',
  featured: false,
});

/** Cosa manca a un tasto per essere salvato (le stesse regole dell'API). */
export function buttonProblem(b: GuestButton): string | null {
  const k = KINDS[b.kind];
  if (k.fixed === 'wifi' && !b.ssid?.trim()) return 'manca il nome della rete';
  if (k.fixed === 'number' && !/^\+?[\d\s./-]{6,24}$/.test(b.number?.trim() ?? ''))
    return 'manca il numero';
  if (!k.fixed && b.tap === 'link' && !/^https?:\/\/\S+$/.test(b.url?.trim() ?? ''))
    return 'manca il link (https://…)';
  if (!k.fixed && b.tap === 'popup' && !b.popupText?.trim()) return 'manca il testo del popup';
  return null;
}

/** Il tasto pulito per l'API: solo i campi che servono al suo tipo e a cosa fa al tocco. */
export function cleanButton(b: GuestButton): GuestButton {
  const k = KINDS[b.kind];
  const out: GuestButton = {
    id: b.id,
    kind: b.kind,
    label: b.label.trim(),
    value: b.value.trim(),
    tap: k.fixed ? 'none' : b.tap,
    featured: b.featured,
    hidden: Boolean(b.hidden),
  };
  if (k.fixed === 'wifi') {
    out.ssid = b.ssid?.trim() ?? '';
    if (b.pass) out.pass = b.pass;
  }
  if (k.fixed === 'number') out.number = b.number?.trim() ?? '';
  if (!k.fixed && b.tap === 'link') out.url = b.url?.trim() ?? '';
  if (!k.fixed && b.tap === 'popup') {
    if (b.popupTitle?.trim()) out.popupTitle = b.popupTitle.trim();
    out.popupText = b.popupText?.trim() ?? '';
  }
  return out;
}

const small = 'text-label font-medium uppercase tracking-[0.04em] text-muted';

/**
 * Benvenuto della pagina per gli ospiti: la frase (vuota: quella standard del sito, tradotta) e
 * l'immagine bassa in testa, caricata come quelle della scheda (`POST /guests/{slug}/image`).
 */
export function WelcomeCard({
  slug,
  welcome,
  onWelcome,
  banner,
  onBanner,
  bannerVisible,
  onBannerVisible,
}: {
  /** struttura già salvata (l'immagine si carica solo allora) */
  slug: string | null;
  welcome: string;
  onWelcome: (v: string) => void;
  banner: GuestBanner | null;
  onBanner: (v: GuestBanner | null) => void;
  /** l'immagine si vede sulla pagina (`sections.banner`) */
  bannerVisible: boolean;
  onBannerVisible: (v: boolean) => void;
}) {
  const { api, config } = useSession();
  const media = config.mediaUrl ?? config.siteUrl;
  const file = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const upload = async (f: File) => {
    if (!slug) return;
    const type = mimeOf(f);
    if (!type || type === 'application/pdf')
      return setMsg('Immagine non valida: JPEG, PNG, WebP o HEIC.');
    if (f.size > MAX_UPLOAD_MB * 1024 * 1024) return setMsg(`Immagine oltre ${MAX_UPLOAD_MB} MB.`);
    setMsg(null);
    try {
      setBusy('Caricamento… 0%');
      const key = await uploadOriginal(api, f, type, (pct) => setBusy(`Caricamento… ${pct}%`));
      setBusy('Preparo le misure dell’immagine…');
      const { image } = await api<{ image: GuestBanner }>(`/guests/${slug}/image`, {
        method: 'POST',
        body: { uploadKey: key },
      });
      onBanner(image);
      setMsg('Immagine pronta: si vede sul sito dopo il salvataggio.');
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card title="Benvenuto">
      <Field
        label="Frase di benvenuto"
        hint={`Vuota: si usa la frase standard, già tradotta. La vostra si traduce in automatico. ${welcome.length}/240`}
      >
        <textarea
          className={`${inputCls} h-20 py-2`}
          value={welcome}
          maxLength={240}
          placeholder={DEFAULT_WELCOME}
          onChange={(e) => onWelcome(e.target.value)}
        />
      </Field>
      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <span className={small}>Immagine in testa (facoltativa)</span>
          {banner && <VisibleToggle visible={bannerVisible} onChange={onBannerVisible} />}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {banner && (
            <img
              src={`${media}/media/${banner.key}-800.webp`}
              alt="Immagine in testa"
              className={`h-16 w-56 object-cover ${bannerVisible ? '' : 'opacity-40'}`}
            />
          )}
          <button
            type="button"
            className={btnSecondary}
            disabled={!slug || !!busy}
            title={slug ? undefined : 'Salva la struttura per caricare l’immagine'}
            onClick={() => file.current?.click()}
          >
            <ImagePlus className="size-4" aria-hidden />
            {banner ? 'Cambia immagine' : 'Carica un’immagine'}
          </button>
          {banner && (
            <button type="button" className={btnSecondary} onClick={() => onBanner(null)}>
              <X className="size-4" aria-hidden /> Togli
            </button>
          )}
          <input
            ref={file}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
        </div>
        <span className="mt-1 block text-xs text-muted">
          Una foto larga e bassa: sul telefono è alta 110 px (180 da computer) e si ritaglia al
          centro. Il panorama, la facciata, la piscina.
        </span>
        {(busy || msg) && <p className="mt-2 text-sm">{busy ?? msg}</p>}
      </div>
    </Card>
  );
}

/**
 * Tasti sotto il benvenuto (fino a 6): tipo, etichetta, testo sul tasto, cosa fanno al tocco,
 * uno in evidenza (stella), l'ordine; accanto l'anteprima da telefono.
 */
export function ButtonsCard({
  buttons,
  onChange,
  visible,
  onVisible,
}: {
  buttons: GuestButton[];
  onChange: (v: GuestButton[]) => void;
  /** i tasti si vedono sulla pagina (`sections.buttons`) */
  visible: boolean;
  onVisible: (v: boolean) => void;
}) {
  const patch = (i: number, p: Partial<GuestButton>) =>
    onChange(buttons.map((b, j) => (j === i ? { ...b, ...p } : b)));
  const move = (i: number) => {
    if (i === 0) return;
    const next = buttons.slice();
    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
    onChange(next);
  };
  const seg = (on: boolean) =>
    `h-9 px-3 text-sm ${on ? 'bg-fg text-page' : 'bg-page hover:bg-surface-strong'}`;

  return (
    <Card
      title={`Tasti sotto il benvenuto (${buttons.length} di ${MAX_BUTTONS})`}
      actions={
        <VisibleToggle
          visible={visible}
          onChange={onVisible}
          hiddenLabel="Nascosti: restano salvati"
        />
      }
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="flex min-w-0 flex-col gap-3">
          {buttons.length === 0 && (
            <p className="text-sm text-muted">
              Nessun tasto: sotto il benvenuto arriva subito il meteo.
            </p>
          )}
          {buttons.map((b, i) => {
            const k = KINDS[b.kind];
            const problem = buttonProblem(b);
            return (
              <div
                key={b.id}
                className={`flex flex-col gap-3 p-3 ${b.featured ? 'border-2 border-fg' : 'border border-surface-strong'} ${b.hidden ? 'bg-surface' : ''}`}
              >
                {b.hidden && (
                  <p className="text-xs font-medium text-muted">
                    Nascosto: resta salvato ma non si vede sulla pagina.
                  </p>
                )}
                <div className="grid gap-2 sm:grid-cols-[140px_minmax(0,1fr)_minmax(0,1.4fr)] sm:items-end">
                  <Field label="Tipo">
                    <select
                      className={inputCls}
                      value={b.kind}
                      onChange={(e) => {
                        const kind = e.target.value as ButtonKind;
                        patch(i, { ...blank(kind), id: b.id, featured: b.featured });
                      }}
                    >
                      {KIND_ORDER.map((kind) => (
                        <option key={kind} value={kind}>
                          {KINDS[kind].name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Etichetta">
                    <input
                      className={inputCls}
                      value={b.label}
                      maxLength={20}
                      placeholder={k.label}
                      onChange={(e) => patch(i, { label: e.target.value })}
                    />
                  </Field>
                  <Field label="Testo sul tasto">
                    <input
                      className={inputCls}
                      value={b.value}
                      maxLength={40}
                      placeholder={k.value || 'es. 8:00 – 10:30'}
                      onChange={(e) => patch(i, { value: e.target.value })}
                    />
                  </Field>
                  <div className="order-first flex justify-end gap-1 sm:col-span-3">
                    <button
                      type="button"
                      aria-label="In evidenza"
                      aria-pressed={b.featured}
                      title="In evidenza: in cima, a tutta riga, più grande (uno solo)"
                      onClick={() =>
                        onChange(
                          buttons.map((x, j) => ({
                            ...x,
                            featured: j === i ? !x.featured : false,
                          })),
                        )
                      }
                      className={`${btnGhost} h-10 w-10 justify-center ${b.featured ? 'bg-accent' : 'bg-surface'}`}
                    >
                      <Star
                        className="size-4"
                        fill={b.featured ? 'currentColor' : 'none'}
                        aria-hidden
                      />
                    </button>
                    <button
                      type="button"
                      aria-label={b.hidden ? 'Mostra il tasto' : 'Nascondi il tasto'}
                      aria-pressed={!b.hidden}
                      title={
                        b.hidden
                          ? 'Mostra il tasto sulla pagina'
                          : 'Nascondi il tasto (resta salvato)'
                      }
                      onClick={() => patch(i, { hidden: !b.hidden })}
                      className={`${btnGhost} h-10 w-10 justify-center bg-surface`}
                    >
                      {b.hidden ? (
                        <EyeOff className="size-4" aria-hidden />
                      ) : (
                        <Eye className="size-4" aria-hidden />
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label="Sposta su"
                      disabled={i === 0}
                      onClick={() => move(i)}
                      className={`${btnGhost} h-10 w-10 justify-center bg-surface`}
                    >
                      <ArrowUp className="size-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      aria-label="Togli il tasto"
                      onClick={() => onChange(buttons.filter((_, j) => j !== i))}
                      className={`${btnGhost} h-10 w-10 justify-center bg-surface`}
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-2">
                  <span className={`w-16 self-center ${small}`}>Al tocco</span>
                  {k.fixed === 'wifi' && (
                    <>
                      <Field label="Nome della rete" className="min-w-40 flex-1">
                        <input
                          className={inputCls}
                          value={b.ssid ?? ''}
                          maxLength={64}
                          onChange={(e) => patch(i, { ssid: e.target.value })}
                        />
                      </Field>
                      <Field label="Password" className="min-w-40 flex-1">
                        <input
                          className={`${inputCls} font-mono`}
                          value={b.pass ?? ''}
                          maxLength={64}
                          autoComplete="off"
                          onChange={(e) => patch(i, { pass: e.target.value })}
                        />
                      </Field>
                      <span className="w-full text-xs text-muted">
                        Sempre un popup con il nome della rete e la password, ciascuno da copiare
                        con un tocco. La password è visibile a chi apre la pagina.
                      </span>
                    </>
                  )}
                  {k.fixed === 'number' && (
                    <>
                      <Field label="Numero" className="min-w-48 flex-1">
                        <input
                          className={inputCls}
                          value={b.number ?? ''}
                          placeholder="+39 …"
                          inputMode="tel"
                          onChange={(e) => patch(i, { number: e.target.value })}
                        />
                      </Field>
                      <span className="w-full text-xs text-muted">{k.note}</span>
                    </>
                  )}
                  {k.fixed === 'nav' && (
                    <span className="flex-1 py-2 text-sm">
                      Apre il navigatore fino alla struttura, con la posizione della scheda: niente
                      da scrivere.
                    </span>
                  )}
                  {!k.fixed && (
                    <>
                      <div
                        role="radiogroup"
                        aria-label="Al tocco"
                        className="flex border border-fg"
                      >
                        {(
                          [
                            ['none', 'Niente'],
                            ['link', 'Link esterno'],
                            ['popup', 'Popup con info'],
                          ] as const
                        ).map(([v, text]) => (
                          <button
                            key={v}
                            type="button"
                            role="radio"
                            aria-checked={b.tap === v}
                            onClick={() => patch(i, { tap: v })}
                            className={seg(b.tap === v)}
                          >
                            {text}
                          </button>
                        ))}
                      </div>
                      {b.tap === 'none' && (
                        <span className="min-w-48 flex-1 pb-2 text-xs text-muted">
                          Il tasto mostra solo etichetta e testo: non si tocca.
                        </span>
                      )}
                      {b.tap === 'link' && (
                        <Field label="Indirizzo web" className="min-w-56 flex-1">
                          <input
                            className={inputCls}
                            value={b.url ?? ''}
                            placeholder="https://…"
                            inputMode="url"
                            onChange={(e) => patch(i, { url: e.target.value })}
                          />
                        </Field>
                      )}
                      {b.tap === 'popup' && (
                        <div className="grid w-full gap-2">
                          <Field label="Titolo del popup">
                            <input
                              className={inputCls}
                              value={b.popupTitle ?? ''}
                              maxLength={60}
                              placeholder={b.label}
                              onChange={(e) => patch(i, { popupTitle: e.target.value })}
                            />
                          </Field>
                          <Field
                            label="Testo del popup"
                            hint="Una riga vuota fa un nuovo paragrafo."
                          >
                            <textarea
                              className={`${inputCls} h-20 py-2`}
                              value={b.popupText ?? ''}
                              maxLength={600}
                              onChange={(e) => patch(i, { popupText: e.target.value })}
                            />
                          </Field>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {problem && (
                  <p className="text-xs font-medium text-danger">Da completare: {problem}</p>
                )}
              </div>
            );
          })}
          {buttons.length < MAX_BUTTONS && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted">Aggiungi:</span>
              {KIND_ORDER.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => onChange([...buttons, blank(kind)])}
                  className={`${btnSecondary} h-8 px-2 normal-case tracking-normal`}
                >
                  <Plus className="size-3.5" aria-hidden /> {KINDS[kind].name}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs text-muted">
            La stella mette un tasto in evidenza: in cima, a tutta riga, più grande (uno solo).
            L’occhio nasconde un tasto senza cancellarlo. Etichetta e testo vuoti: si usano quelli
            standard (in grigio), già tradotti in inglese, tedesco, francese e spagnolo; quelli
            scritti da voi si traducono in automatico. Wi-Fi, WhatsApp, Telefono e Navigatore fanno
            sempre la stessa cosa; per gli altri scegliete se non si tocca, apre un link o apre un
            popup. Etichette, testi e popup si traducono in automatico.
          </p>
        </div>
        <ButtonsPreview buttons={visible ? buttons.filter((b) => !b.hidden) : []} />
      </div>
    </Card>
  );
}

/** Anteprima da telefono dei tasti, come sul sito (apps/web/components/guest-buttons.tsx). */
function ButtonsPreview({ buttons }: { buttons: GuestButton[] }) {
  const ordered = [...buttons.filter((b) => b.featured), ...buttons.filter((b) => !b.featured)];
  const plain = ordered.filter((b) => !b.featured).length;
  return (
    <aside aria-label="Anteprima da telefono" className="flex flex-col gap-1.5">
      <span className={small}>Anteprima da telefono</span>
      <div className="grid grid-cols-2 gap-1.5 border border-surface-strong p-2">
        {ordered.length === 0 && (
          <span className="col-span-2 p-2 text-xs text-muted">Nessun tasto</span>
        )}
        {ordered.map((b, i) => {
          const k = KINDS[b.kind];
          const wide = b.featured || (plain % 2 === 1 && i === ordered.length - 1);
          const tap =
            k.fixed === 'wifi' ? 'popup' : k.fixed ? 'out' : b.tap === 'link' ? 'out' : b.tap;
          return (
            <span
              key={b.id}
              className={`flex min-w-0 items-center gap-2 p-2 ${wide ? 'col-span-2' : ''} ${b.featured ? 'border-2 border-fg bg-page' : 'bg-surface'}`}
            >
              <span className={`size-6 shrink-0 ${b.featured ? 'bg-accent' : 'bg-page'}`} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[10px] font-medium uppercase tracking-[0.04em] text-muted">
                  {b.label || k.label}
                </span>
                <span className="truncate text-xs font-medium">{b.value || k.value || '…'}</span>
              </span>
              {tap === 'popup' && <span aria-hidden>›</span>}
              {tap === 'out' && <span aria-hidden>↗</span>}
            </span>
          );
        })}
      </div>
      <span className="text-xs text-muted">
        › apre un popup · ↗ esce dalla pagina · nessun segno: solo informazione
      </span>
    </aside>
  );
}
